/**
 * The class strings the charity pages outside the campaign share.
 *
 * The palette itself lives in `campaignTheme`; what is added here is the page
 * shell, the panel that replaces a card, and the controls a form page needs.
 * Tailwind reads arbitrary colour values out of the source text, so the classes
 * are written once here rather than assembled from the hex constants.
 */

import { CAMPAIGN_FOCUS_RING_CLASS, CAMPAIGN_PAGE_COLUMN_CLASS, CAMPAIGN_SECTION_CLASS } from "./campaignTheme";

/**
 * The shell a charity page runs in, and the guarantee it never scrolls sideways.
 *
 * Carries no ground of its own. The layout paints it once, beneath the wheel
 * backdrop; a page painting it again would cover the backdrop up.
 *
 * Clipped rather than hidden: `overflow-x: hidden` turns the shell into a scroll
 * container, and a sticky element inside one sticks to a box that never scrolls.
 */
export const CHARITY_PAGE_CLASS = "min-h-screen overflow-x-clip text-[#F6F4F0]";

/** The centred column a charity page's content runs in. */
export const CHARITY_COLUMN_CLASS = "mx-auto w-full max-w-3xl px-5 py-14 sm:px-7";

/**
 * The same column for a page built out of hairline-topped sections.
 *
 * Takes the campaign's own rhythm, so a section on the rules page sits in the
 * same space as a section on the campaign page and no block needs a margin.
 */
export const CHARITY_STACKED_COLUMN_CLASS = `mx-auto w-full max-w-3xl px-5 sm:px-7 ${CAMPAIGN_PAGE_COLUMN_CLASS}`;

/**
 * The same column for a screen that carries one message rather than a page.
 *
 * A wait, a refusal or a confirmation has nothing to scan down, so it reads as
 * a notice in the middle of the page rather than copy ranged against the left
 * edge of a column two thirds the width of the header above it.
 */
export const CHARITY_INTERSTITIAL_COLUMN_CLASS = `${CHARITY_COLUMN_CLASS} text-center`;

/** The page's own title, set at the weight the campaign hero uses. */
export const CHARITY_PAGE_TITLE_CLASS =
  "m-0 text-[30px] font-extrabold uppercase leading-[1.02] tracking-[-0.03em] sm:text-[38px]";

/** The line that sits under a page title. */
export const CHARITY_PAGE_LEAD_CLASS = "mt-2 text-[14.5px] leading-relaxed text-[#A2A09C]";

/**
 * A division of a page, titled in the page title's own manner.
 *
 * Set uppercase at the page title's weight and tracking, a step down in size.
 * Sharing the treatment is what makes a section read as a division of the page
 * rather than a block sitting on it; the size is what keeps the page's own
 * title first.
 */
export const CHARITY_SECTION_TITLE_CLASS =
  "m-0 text-[21px] font-extrabold uppercase leading-[1.05] tracking-[-0.02em] sm:text-[25px]";

/** Raised panel: what a card becomes on the dark ground. */
export const CHARITY_PANEL_CLASS = "rounded-md border border-[#2C2C30] bg-[#1C1C1F] p-4 sm:p-5";

/** Panel carrying something a reader must not skip past. */
export const CHARITY_ALERT_PANEL_CLASS = "rounded-md border border-[#E66E1A] bg-[#1C1C1F] p-4 sm:p-5";

/** One entry in a list of members, reports or riders. */
export const CHARITY_ROW_CLASS = "rounded-[5px] border border-[#2C2C30] bg-[#1C1C1F] p-3.5 sm:p-4";

/** Uppercase micro-label naming a field. */
export const CHARITY_FIELD_LABEL_CLASS =
  "mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.11em] text-[#A2A09C]";

/** The quiet line under a field, saying what the field is for. */
export const CHARITY_FIELD_NOTE_CLASS = "mt-1.5 text-[12px] leading-relaxed text-[#7C7A76]";

/** Uppercase caption under a figure. */
export const CHARITY_FIGURE_LABEL_CLASS = "text-[10.5px] uppercase tracking-[0.11em] text-[#A2A09C]";

/** A figure, aligned on its digits so a column of them reads straight. */
export const CHARITY_FIGURE_VALUE_CLASS = "text-[17px] font-extrabold tabular-nums tracking-[-0.02em] text-[#F6F4F0]";

/**
 * Single-line text input.
 *
 * Declares a dark colour scheme so the browser's own furniture — the date
 * field's calendar button and its picker — is drawn to match rather than
 * arriving as a black icon on a black field.
 */
export const CHARITY_INPUT_CLASS = `h-11 w-full rounded-[5px] border border-[#2C2C30] bg-[#1C1C1F] px-3 text-[14.5px] text-[#F6F4F0] shadow-none [color-scheme:dark] placeholder:text-[#7C7A76] focus-visible:ring-0 ${CAMPAIGN_FOCUS_RING_CLASS}`;

/** Multi-line text input, on the same treatment as the single-line one. */
export const CHARITY_TEXTAREA_CLASS = `w-full rounded-[5px] border border-[#2C2C30] bg-[#1C1C1F] px-3 py-2.5 text-[14.5px] leading-relaxed text-[#F6F4F0] [color-scheme:dark] placeholder:text-[#7C7A76] ${CAMPAIGN_FOCUS_RING_CLASS}`;

/** Native checkbox and radio, tinted to the campaign's orange. */
export const CHARITY_CONTROL_CLASS = `h-4 w-4 flex-shrink-0 accent-[#E66E1A] ${CAMPAIGN_FOCUS_RING_CLASS}`;

/** Chip: a fact about a row rather than an action on it. */
export const CHARITY_CHIP_CLASS =
  "inline-flex items-center rounded border border-[#2C2C30] px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A2A09C]";

/** Fields are set narrower than the column, so an input is never a yard wide. */
export const CHARITY_FORM_MEASURE_CLASS = "max-w-md";

/** The same measure on a centred screen, where it is the block that centres rather than the text. */
export const CHARITY_INTERSTITIAL_ACTION_CLASS = `${CHARITY_FORM_MEASURE_CLASS} mx-auto`;

/** Frame a board scrolls inside, so a wide table never takes the page with it. */
export const CHARITY_TABLE_FRAME_CLASS = "overflow-x-auto rounded-md border border-[#2C2C30]";

/** Column heading on a board. */
export const CHARITY_TABLE_HEAD_CELL_CLASS =
  "whitespace-nowrap px-4 py-3 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-[#A2A09C] sm:px-5";

/** Cell on a board. */
export const CHARITY_TABLE_CELL_CLASS = "px-4 py-3.5 text-[14px] sm:px-5";

/** The row of navigation controls that closes a page. */
export const CHARITY_PAGE_FOOTER_CLASS = "mt-14 flex flex-col gap-3 border-t border-[#2C2C30] pt-14 sm:flex-row";

/** The same row on a stacked page, where the column's gap already sets it apart. */
export const CHARITY_STACKED_FOOTER_CLASS = `${CAMPAIGN_SECTION_CLASS} flex flex-col gap-3 sm:flex-row`;

/** Centred single-message state: loading, or a page a visitor cannot see yet. */
export const CHARITY_CENTRED_STATE_CLASS = "flex min-h-[60vh] items-center justify-center px-5 text-center";

/** The dimmed page behind an open dialog. */
export const CHARITY_MODAL_OVERLAY_CLASS = "fixed inset-0 z-50 bg-black/70";

/**
 * The dialog itself.
 *
 * Capped in height and scrolled inside, because the ride form on a short phone
 * in landscape is taller than the viewport and a dialog that cannot scroll
 * hides its own submit button.
 */
export const CHARITY_MODAL_PANEL_CLASS =
  "fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md border border-[#2C2C30] bg-[#131315] p-5 text-[#F6F4F0] shadow-xl sm:p-6";

/**
 * A dialog carrying reference detail rather than a form.
 *
 * Wider than the form dialog because what it holds is tables and rule lists,
 * which wrap into unreadable columns at the form's width.
 */
export const CHARITY_WIDE_MODAL_PANEL_CLASS =
  "fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md border border-[#2C2C30] bg-[#131315] p-5 text-[#F6F4F0] shadow-xl sm:p-6";

/** The dialog's own heading. */
export const CHARITY_MODAL_TITLE_CLASS = "text-[17px] font-extrabold tracking-[-0.02em]";

/** Lifts one value out of a sentence — the address a link was sent to, a name, a total. */
export const CHARITY_EMPHASIS_CLASS = "font-semibold text-[#F6F4F0]";
